// InputField.jsx
import React, { useState, useEffect } from "react";
import "./InputField.css";
import Dropdown from "../Dropdown/Dropdown";

const InputField = ({
  label,
  type = "text",
  placeholder,
  value,
  onChange,
  icon: Icon,
  name,
  required = false,
  disabled = false,
  style,
  className = "",
  maxLength,
  onBlur,
  options = [],
  rows = 4,
  multiple = false,
  accept,
  error,
  positiveOnly = false,
  showPrefix = false,
  prefixContent = null,
}) => {
  const [showError, setShowError] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const hasExternalError = Boolean(error);
  const hasValue = String(value ?? "").trim().length > 0;
  const showFloatingLabel = Boolean(label);
  const hasPlaceholder = String(placeholder ?? "").trim().length > 0;
  const hideLabelWhenPlaceholder = showFloatingLabel && hasPlaceholder && !isFocused && !hasValue;
  const shouldShowPrefix = showPrefix && prefixContent !== null && prefixContent !== undefined;

  // Value değiştiğinde hatayı otomatik temizle
  useEffect(() => {
    if (required && String(value || "").trim()) {
      setShowError(false);
    }
  }, [value, required]);

  const handleBlur = (e) => {
    setIsFocused(false);
    if (required && !e.target.value.trim()) {
      setShowError(true);
    } else {
      setShowError(false);
    }
    onBlur?.(e);
  };

  const handleFocus = () => {
    setIsFocused(true);
  };

  const sanitizeNumericValue = (rawValue) => {
    const raw = String(rawValue ?? "");

    if (type === "number") {
      let nextValue = raw.replace(/\D/g, "");
      if (positiveOnly) {
        if (/^0+$/.test(nextValue)) return "";
        nextValue = nextValue.replace(/^0+/, "");
      }
      return nextValue;
    }

    if (type === "float") {
      const normalized = raw.replace(/,/g, ".");
      const cleaned = normalized.replace(/[^\d.]/g, "");
      const firstDotIndex = cleaned.indexOf(".");
      const nextValue =
        firstDotIndex === -1
          ? cleaned
          : `${cleaned.slice(0, firstDotIndex + 1)}${cleaned
              .slice(firstDotIndex + 1)
              .replace(/\./g, "")}`;

      if (!positiveOnly) return nextValue;
      // ARA DEĞER KORUMASI: kullanıcı "0.05" yazarken "0.0" adımından geçer. Bu ara değerler
      // Number(...)=0 olduğu için "" döndürülüp yazılan HER ŞEY siliniyordu. Yalnızca
      // "gerçekten sıfır" (0, 0.00 gibi tamamlanmış) değerler değil, sıfırla başlayan tüm
      // ara girişler korunur; nihai doğrulama form tarafında yapılır.
      if (nextValue === "" || nextValue === "." || nextValue.endsWith(".")) return nextValue;
      if (/^0(\.0*)?$/.test(nextValue)) return nextValue;
      return Number(nextValue) > 0 ? nextValue : "";
    }

    return raw;
  };

  const handleChange = (e) => {
    if (type === "number" || type === "float") {
      e.target.value = sanitizeNumericValue(e.target.value);
    }
    onChange?.(e);
  };

  const handleKeyPress = (e) => {
    const charCode = e.charCode || e.keyCode;
    if (type === "number" && (charCode < 48 || charCode > 57)) {
      e.preventDefault();
    }
    if (type === "float") {
      const char = String.fromCharCode(charCode);
      // Virgül de kabul edilir: TR klavye/numpad'de ondalık ayracı virgüldür.
      // Eskiden virgül burada bloklanıyordu; "2,5" yazan kullanıcıda virgül sessizce
      // yutulup değer "25" olarak kaydediliyordu. sanitizeNumericValue virgülü zaten
      // noktaya çeviriyor, bu yüzden tuşu geçirmek yeterli.
      if (!/[0-9.,]/.test(char)) e.preventDefault();
      if ((char === "." || char === ",") && String(value ?? "").includes(".")) {
        e.preventDefault();
      }
    }
  };

  const commonFieldProps = {
    onBlur: handleBlur,
    onFocus: handleFocus,
    value: value ?? "",
    onChange: handleChange,
    name,
    "aria-required": required ? "true" : "false",
    disabled,
    "aria-label": label || placeholder || name,
  };

  const inputType = type === "number" || type === "float" ? "text" : type;
  const numericInputProps =
    type === "number"
      ? { inputMode: "numeric", pattern: "[0-9]*" }
      : type === "float"
        ? { inputMode: "decimal", pattern: "[0-9]*[.]?[0-9]*" }
        : {};

  return (
    <div
      style={style}
      className={`inputfield-container ${className} ${disabled ? "inputfield-container-disabled" : ""}`}
    >
      <div
        className={`inputfield-wrapper ${Icon ? "has-icon" : ""} ${shouldShowPrefix ? "has-prefix" : ""} ${disabled ? "disabled" : ""} ${
          (showError && !isFocused) || hasExternalError ? "inputfield-error" : ""
        }`}
      >
        {showFloatingLabel && (
          <label
            className={`inputfield-floating-label ${isFocused || hasValue ? "floating" : ""} ${
              disabled ? "disabled" : ""
            } ${hideLabelWhenPlaceholder ? "hidden" : ""}`}
          >
            {label} {required && <span className="inputfield-required">*</span>}
          </label>
        )}
        {Icon && <Icon className="inputfield-icon" size={19} />}
        {type === "textarea" ? (
          <textarea
            {...commonFieldProps}
            rows={rows}
            maxLength={maxLength}
            placeholder={isFocused ? "" : placeholder}
            className="inputfield inputfield-textarea"
          />
        ) : type === "select" ? (
          <Dropdown
            options={options}
            label=""
            placeholder={placeholder}
            value={value ?? ""}
            onChange={onChange}
            onBlur={handleBlur}
            onFocus={handleFocus}
            disabled={disabled}
            className="inputfield-select-dropdown"
          />
        ) : type === "file" ? (
          <input
            {...commonFieldProps}
            type="file"
            multiple={multiple}
            accept={accept}
            className="inputfield"
          />
        ) : (
          <>
            {shouldShowPrefix && <span className="inputfield-prefix">{prefixContent}</span>}
            <input
              {...commonFieldProps}
              {...numericInputProps}
              type={inputType}
              placeholder={isFocused ? "" : placeholder}
              maxLength={maxLength}
              onKeyPress={handleKeyPress}
              className="inputfield"
            />
          </>
        )}
      </div>
      {hasExternalError && typeof error === "string" && (
        <span className="inputfield-error-message">{error}</span>
      )}
    </div>
  );
};

export default InputField;
